import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Grid, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { grey } from '@mui/material/colors';
import FlagIcon from '@mui/icons-material/Flag';
import clsx from 'clsx';
import { useNavigate, useLocation } from 'react-router-dom';
import qs from 'qs';
import isNull from 'lodash/isNull';

import { Loader } from 'ui';
import { FULL_API_URL } from 'utils';
import { formatDate, scrollToRef, formatCurrency } from 'shared/functions';

import { isNil } from 'lodash';
import { subscriptionStatuses } from '../../shared/constants/shopSubscriptionStatuses';
import { shopProductsCategories } from '../../shared/constants/shopProductsCategories';
import { shopViews } from '../../shared/constants/shopViews';

import {
  changeShopProductsFavoriteStatusAction,
  addShopProductsToBasketAction,
  startShopProductFreeTrialAction,
  startShopProductFreeSubscriptionAction,
} from '../../store/shopActions';
import { ShopProductIndicator } from './ShopProductIndicator';
import { ShopProductDetails } from './ShopProductDetails';
import { ShopProductTrialModal } from './ShopProductTrialModal';
import { ShopProductFreeSubscriptionModal } from './ShopProductFreeSubscriptionModal';

const useStyles = makeStyles((theme) => ({
  name: {
    fontSize: 28,
    backgroundColor: grey[600],
    color: theme.palette.primary.contrastText,
    borderRadius: '0 4px 0 0',
  },
  container: {
    height: 400,
  },
  label: {
    color: theme.palette.primary.main,
    fontSize: 14,
  },
  infoContainer: {
    height: 336,
    paddingLeft: theme.spacing(8),
  },
  info: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),

    '&:nth-child(odd)': {
      paddingRight: 10,
    },

    '&:nth-child(even)': {
      paddingLeft: 20,
    },
  },
  value: {
    fontWeight: 'bold',
    fontSize: 18,
  },
  labelBar: {
    backgroundColor: grey[400],
    height: '100%',
    borderRadius: '4px 0 0 4px',
    color: theme.palette.primary.contrastText,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: 24,
  },
  imageBar: {
    height: 400,
  },
  image: {
    width: '100%',
    height: '100%',
  },
  thirdPartyImage: {
    maxWidth: '50%',
    maxHeight: '50%',
  },
  imageDivider: {
    backgroundColor: theme.palette.text.primary,
    width: 250,
    margin: '30px auto',
  },
  products: {
    display: 'flex',
    height: 336,
    borderStyle: 'solid',
    borderWidth: '0 5px 0 5px',
    borderColor: grey[200],
    paddingTop: 15,
    paddingBottom: 15,
  },
  product: {
    marginTop: 15,
    '&:first-of-type': {
      marginTop: 0,
    },
  },
  actionContainer: {
    height: '100%',
  },
  priceBox: {
    width: 160,
    borderStyle: 'dashed',
    borderColor: grey[600],
    borderWidth: '0 0 1px 0',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexDirection: 'column',
  },
  price: {
    fontSize: 28,
    fontWeight: 'bold',
  },
  pricePeriod: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  buttonContainer: {
    textAlign: 'center',
  },
  subscriptionButton: {
    width: 160,
    height: 45,
    marginBottom: 10,
  },
  subscriptionButtonActive: {
    backgroundColor: '#ff8900',
  },
  moreInfoButton: {
    height: 45,
    marginBottom: 10,
  },
  favoriteButton: {
    width: 45,
    height: 45,
    padding: 0,
    minWidth: 'auto',
    marginLeft: 6,
    marginBottom: 10,
  },
  marketLabel: {
    maxWidth: 250,
    fontSize: 20,
    fontWeight: 'bold',
  },
  trialText: {
    display: 'block',
    maxWidth: 160,
    fontWeight: 'bold',
    textAlign: 'center',
    padding: 0,
  },
  freeText: {
    fontSize: 22,
  },
  hiddenCheckoutButton: {
    visibility: 'hidden',
  },
  loader: {
    display: 'inline-block',
    position: 'relative',
    margin: '0 10px',
    top: 5,
  },
  esgAnalysis: { backgroundColor: theme.palette.custom.esgAnalysisEvents },
  fundAnalysis: { backgroundColor: theme.palette.custom.fundAnalysisEvents },
  creditAnalysis: { backgroundColor: theme.palette.custom.creditAnalysisEvents },
}));

const ShopProduct = ({ data, preview, collapsed }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const productRef = useRef(null);

  const [isFreeTrialModalOpen, setIsFreeTrialModalOpen] = useState(false);

  const toggleFreeTrialModal = () => {
    setIsFreeTrialModalOpen((isOpen) => !isOpen);
  };

  const [isFreeSubscriptionModalOpen, setIsFreeSubscriptionModalOpen] = useState(false);

  const toggleFreeSubscriptionModal = () => {
    setIsFreeSubscriptionModalOpen((isOpen) => !isOpen);
  };

  const [isAddingToBasket, setIsAddingToBasket] = useState(false);

  const [isFavoriteStatusLoading, setIsFavoriteStatusLoading] = useState(false);

  const { favorites } = useSelector(({ shop }) => shop.history);

  const [showDetails, setShowDetails] = useState(collapsed);

  const { productId: scrollToProductId, freeTrial: freeTrialOpen } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });

  useEffect(() => {
    if (data && scrollToProductId && Number(scrollToProductId) === data.id) {
      setIsFreeTrialModalOpen(!!freeTrialOpen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freeTrialOpen, data, scrollToProductId]);

  const isEsgAnalysis =
    data.categories.length === 1 && data.categories[0] === shopProductsCategories.esgAnalysis;
  const isFundAnalysis =
    data.categories.length === 1 && data.categories[0] === shopProductsCategories.fundAnalysis;
  const isCreditAnalysis =
    data.categories.length === 1 && data.categories[0] === shopProductsCategories.creditAnalysis;

  const isFavorite = favorites.includes(data.id);

  const scrollToProduct = () => {
    scrollToRef(productRef);
  };

  const changeFavoriteStatus = () => {
    setIsFavoriteStatusLoading(true);

    dispatch(
      changeShopProductsFavoriteStatusAction({
        ids: [data.id],
        isFavorite: !favorites.includes(data.id),
      }),
    );
  };

  const addToBasket = () => {
    setIsAddingToBasket(true);

    dispatch(addShopProductsToBasketAction({ ids: [data.id] }));

    navigate({
      search: `?tab=${shopViews.basket}`,
    });
  };

  const getPrice = () => {
    const pricing = (data.pricing ? data.pricing : []).find(({ active }) => active);

    return pricing ? pricing.price : null;
  };

  const getPriceText = () => {
    const price = getPrice();

    if (isNull(price)) {
      return t('On request');
    }

    return price === 0 ? t('Free of charge') : formatCurrency(price, 'EUR');
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const getLogoUrl = (logo) => {
    if (!logo) {
      return '';
    }
    return logo.startsWith('blob') ? logo : `${FULL_API_URL}${data.logo}`;
  };

  const activateFreeTier = () => {
    dispatch(startShopProductFreeTrialAction({ id: data.id }));
  };

  const activateFreeSubscription = () => {
    dispatch(startShopProductFreeSubscriptionAction({ id: data.id }));
  };

  useEffect(() => {
    let timer = null;

    if (showDetails) {
      timer = setTimeout(() => scrollToProduct(), 300);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [showDetails]);

  useEffect(() => {
    if (scrollToProductId && Number(scrollToProductId) === data.id && productRef.current) {
      setShowDetails(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productRef.current]);

  useEffect(() => {
    setIsFavoriteStatusLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFavorite]);

  const price = getPrice();

  const isCheckoutDisabled = Boolean(data.purchased || preview || data.cancelled);

  const messageByStatus = {
    [subscriptionStatuses.ACTIVE_TRIAL]: t('Free trial active'),
    [subscriptionStatuses.ACTIVE_CANCELLED]:
      t('Cancelled. Subscription valid until: ') + formatDate(data.validUntil),
    [subscriptionStatuses.ENDED_CANCELLED]: t('Subscription has ended'),
    [subscriptionStatuses.ENDED_TRIAL]: t('Free trial has ended'),
    [subscriptionStatuses.ACTIVE_TRIAL]: t('Free trial active'),
  };

  const logoUrl = getLogoUrl(data.logo);

  const getIndustryName = (product) => {
    if (Array.isArray(product.industries)) {
      return product.industries.length
        ? product.industries.map(({ name }) => name).join(', ')
        : t('SUBSCRIPTIONS.ALL');
    }
    return data.industries;
  };

  const showSegments = (segments) => {
    if (Array.isArray(segments)) {
      return segments.length > 1 ? t('SUBSCRIPTIONS.VARIOUS') : segments[0];
    }
    return segments;
  };

  return (
    <Box mb={6} ref={productRef}>
      <Paper elevation={3}>
        <Grid container>
          <Grid item xs={3} className={classes.container}>
            <Grid
              container
              className={clsx({
                [classes.labelBar]: true,
                [classes.esgAnalysis]: isEsgAnalysis,
                [classes.fundAnalysis]: isFundAnalysis,
                [classes.creditAnalysis]: isCreditAnalysis,
              })}
            >
              <Grid item>
                <Box className={classes.imageBar}>
                  {logoUrl && <img alt={t('Logo')} src={logoUrl} className={classes.image} />}
                </Box>
              </Grid>
              <Grid
                item
                className={clsx({
                  [classes.esgAnalysis]: isEsgAnalysis,
                  [classes.fundAnalysis]: isFundAnalysis,
                  [classes.creditAnalysis]: isCreditAnalysis,
                })}
              >
                {data.label}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={9}>
            <Grid container>
              <Grid item xs={12}>
                <Box
                  p={2}
                  className={clsx({
                    [classes.name]: true,
                    [classes.esgAnalysis]: isEsgAnalysis,
                    [classes.fundAnalysis]: isFundAnalysis,
                    [classes.creditAnalysis]: isCreditAnalysis,
                  })}
                >
                  <Typography variant="h5">{data.name}</Typography>
                </Box>
              </Grid>
              <Grid item xs={5}>
                <Grid
                  container
                  alignItems="center"
                  justifyContent="center"
                  className={classes.infoContainer}
                >
                  <Grid item>
                    <Grid container>
                      <Grid item xs={6} className={classes.info}>
                        <Typography className={classes.label}>
                          {t('SUBSCRIPTIONS.CATEGORY')}
                        </Typography>
                        <Typography className={classes.value}>
                          {data.categories.join(', ')}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} className={classes.info}>
                        <Typography className={classes.label}>
                          {t('SUBSCRIPTIONS.REGION')}
                        </Typography>
                        <Typography className={classes.value}>
                          {data.regions?.length > 0
                            ? data.regions.join(', ')
                            : [t('SUBSCRIPTIONS.GLOBAL')].join(', ')}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} className={classes.info}>
                        <Typography className={classes.label}>
                          {t('SUBSCRIPTIONS.SEGMENT')}
                        </Typography>
                        <Typography className={classes.value}>
                          {data.segments?.length > 0
                            ? showSegments(data.segments)
                            : [t('SUBSCRIPTIONS.ALL')].join(', ')}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} className={classes.info}>
                        <Typography className={classes.label}>
                          {t('SUBSCRIPTIONS.INDUSTRY')}
                        </Typography>
                        <Typography className={classes.value}>
                          {data.industries?.length > 1
                            ? t('SUBSCRIPTIONS.VARIOUS')
                            : getIndustryName(data)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} className={classes.info}>
                        <Typography className={classes.marketLabel}>{data.marketLabel}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={4}>
                <Box pl={6} className={classes.products}>
                  <Grid container>
                    {(data.products || []).map((product) => (
                      <Grid item xs={12} className={classes.product} key={product.type}>
                        <ShopProductIndicator product={product} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                  direction="column"
                  className={classes.actionContainer}
                >
                  <Grid item>
                    <Box className={classes.priceBox} pb={2} mt={2}>
                      <Typography
                        component="span"
                        className={clsx({
                          [classes.price]: true,
                          [classes.freeText]: price === 0,
                        })}
                      >
                        {getPriceText()}
                      </Typography>
                      {data.validity && price !== 0 && !isNull(price) && (
                        <Typography
                          component="span"
                          variant="body2"
                          className={classes.pricePeriod}
                        >
                          {data.validity}
                        </Typography>
                      )}
                    </Box>
                    <Box mt={2} mb={1}>
                      <Typography component="span" className={classes.trialText}>
                        {data.status === subscriptionStatuses.NO_SUBSCRIPTION && price === 0
                          ? t('Start your free subscription here')
                          : messageByStatus[data.status]}
                      </Typography>
                    </Box>
                    {![subscriptionStatuses.ACTIVE, subscriptionStatuses.ACTIVE_TRIAL].includes(
                      data.status,
                    ) &&
                      !data.cancelled &&
                      data.isFreeTrial &&
                      !data.trialStarted && (
                        <Box mt={2} mb={1}>
                          <Typography component="span" className={classes.trialText}>
                            {t('Get your free {{period}} weeks trial here', {
                              period: data.trialPeriod / 7,
                            })}
                          </Typography>
                        </Box>
                      )}
                  </Grid>
                  <Grid item>
                    <Box className={classes.buttonContainer}>
                      {data.isFreeTrial && !data.trialStarted && (
                        <>
                          <Button
                            variant="outlined"
                            color="primary"
                            className={classes.subscriptionButton}
                            onClick={toggleFreeTrialModal}
                            disabled={data.purchased || preview}
                          >
                            {data.purchased
                              ? t('SUBSCRIPTIONS.SUBSCRIBED')
                              : t('SUBSCRIPTIONS.GET_FREE_TRIAL')}
                          </Button>
                          <ShopProductTrialModal
                            open={isFreeTrialModalOpen}
                            onConfirm={activateFreeTier}
                            onClose={toggleFreeTrialModal}
                            data={data}
                          />
                        </>
                      )}
                      {price === 0 && (
                        <>
                          <Button
                            variant="outlined"
                            color="primary"
                            className={classes.subscriptionButton}
                            onClick={toggleFreeSubscriptionModal}
                            disabled={data.purchased || preview}
                          >
                            {data.purchased
                              ? t('SUBSCRIPTIONS.SUBSCRIBED')
                              : t('SUBSCRIPTIONS.GET_STARTED')}
                          </Button>
                          {!preview && (
                            <ShopProductFreeSubscriptionModal
                              open={isFreeSubscriptionModalOpen}
                              onConfirm={activateFreeSubscription}
                              onClose={toggleFreeSubscriptionModal}
                              data={data}
                            />
                          )}
                        </>
                      )}
                      <Button
                        variant={showDetails ? 'contained' : 'outlined'}
                        color="primary"
                        className={classes.moreInfoButton}
                        onClick={toggleDetails}
                        disabled={preview}
                      >
                        {t('SUBSCRIPTIONS.MORE_INFO')}
                      </Button>
                      {isFavoriteStatusLoading ? (
                        <Loader size={30} classes={classes.loader} />
                      ) : (
                        <Button
                          variant={isFavorite ? 'contained' : 'outlined'}
                          color="primary"
                          className={classes.favoriteButton}
                          onClick={changeFavoriteStatus}
                          disabled={preview}
                        >
                          <FlagIcon />
                        </Button>
                      )}

                      <Button
                        variant="contained"
                        color="primary"
                        className={clsx({
                          [classes.subscriptionButton]: true,
                          [classes.subscriptionButtonActive]: true,
                          [classes.hiddenCheckoutButton]:
                            price === 0 || (isNil(price) && !data.purchased),
                        })}
                        onClick={addToBasket}
                        disabled={isCheckoutDisabled || isAddingToBasket}
                      >
                        {data.purchased
                          ? t('SUBSCRIPTIONS.SUBSCRIBED')
                          : t('SUBSCRIPTIONS.ADD_TO_BASKET')}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <ShopProductDetails product={data} expanded={showDetails} preview={preview} />
      </Paper>
    </Box>
  );
};

ShopProduct.defaultProps = {
  preview: false,
  collapsed: false,
};

ShopProduct.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    categories: PropTypes.arrayOf(PropTypes.string).isRequired,
    regions: PropTypes.arrayOf(PropTypes.string).isRequired,
    segments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
      }),
    ).isRequired,
    industries: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
      }),
    ).isRequired,
    sources: PropTypes.arrayOf(PropTypes.string).isRequired,
    amount: PropTypes.number,
    validity: PropTypes.string,
    products: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        availability: PropTypes.bool.isRequired,
        upgrade: PropTypes.bool.isRequired,
      }),
    ).isRequired,
    subscribedUntil: PropTypes.string,
    pricing: PropTypes.arrayOf(
      PropTypes.shape({
        price: PropTypes.number.isRequired,
        activationFee: PropTypes.number.isRequired,
        tier: PropTypes.number,
        active: PropTypes.bool,
      }),
    ),
    label: PropTypes.string,
    additionalInfo: PropTypes.string,
    purchased: PropTypes.bool.isRequired,
    logo: PropTypes.string,
    sourceLogo: PropTypes.string,
    isFreeTrial: PropTypes.bool,
    trialAmount: PropTypes.number,
    trialStarted: PropTypes.bool,
    marketLabel: PropTypes.string,
    trialPeriod: PropTypes.number,
    cancelled: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    validUntil: PropTypes.string,
    status: PropTypes.string.isRequired,
  }).isRequired,
  preview: PropTypes.bool,
  collapsed: PropTypes.bool,
};

export { ShopProduct };
